import React, {
	useState,
	useEffect,
	useMemo,
	useCallback,
	useRef,
} from 'react';
import {
	Box,
	Fab,
	Paper,
	Typography,
	IconButton,
	Fade,
	styled,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import SocratesOfferDialog from './SocratesOfferDialog';
import storageService from 'src/services/storage-service';
import { AddOnType } from 'src/constants/AddOnType';
import { AddOnAccountType } from 'src/constants/AddOnType';

// Styled components using MUI's styled API
const StyledFab = styled(Fab)(({ theme }) => ({
	position: 'fixed',
	bottom: 100,
	left: 30,
	zIndex: 1200,
	width: 80,
	height: 80,
	background: 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)',
	boxShadow:
		'0 8px 25px rgba(33, 150, 243, 0.3), 0 4px 12px rgba(33, 150, 243, 0.2)',
	border: 'none',
	willChange: 'transform, box-shadow',
	backfaceVisibility: 'hidden',
	'&:hover': {
		transform: 'translate3d(0, -3px, 0) scale(1.02)',
		boxShadow:
			'0 12px 35px rgba(33, 150, 243, 0.4), 0 6px 20px rgba(33, 150, 243, 0.3)',
		background: 'linear-gradient(135deg, #1976D2 0%, #1565C0 100%)',
	},
	'&:active': {
		transform: 'translate3d(0, -1px, 0) scale(0.98)',
	},
	transition:
		'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1), background 0.2s ease',
	[theme.breakpoints.down('md')]: {
		bottom: 20,
		left: 20,
		width: 70,
		height: 70,
	},
}));

const StyledIcon = styled('img')(({ theme }) => ({
	width: 80,
	height: 80,
	borderRadius: '50%',
	objectFit: 'cover',
	willChange: 'transform',
	backfaceVisibility: 'hidden',
	transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
	'&:hover': {
		transform: 'scale3d(1.1, 1.1, 1)',
	},
	[theme.breakpoints.down('md')]: {
		width: 70,
		height: 70,
	},
}));

const MessagePaper = styled(Paper)(({ theme }) => ({
	width: 380,
	maxWidth: '90vw',
	borderRadius: 20,
	boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 4px 16px rgba(0, 0, 0, 0.08)',
	border: 'none',
	overflow: 'visible',
	background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
	'&::before': {
		content: '""',
		position: 'absolute',
		bottom: 20,
		left: -8,
		width: 0,
		height: 0,
		borderTop: '8px solid transparent',
		borderBottom: '8px solid transparent',
		borderRight: '8px solid #f8f9fa',
		filter: 'drop-shadow(-2px 0px 4px rgba(0, 0, 0, 0.1))',
	},
	'&::after': {
		content: '""',
		position: 'absolute',
		bottom: 20,
		left: -7,
		width: 0,
		height: 0,
		borderTop: '8px solid transparent',
		borderBottom: '8px solid transparent',
		borderRight: '8px solid #f8f9fa',
	},
	[theme.breakpoints.down('md')]: {
		width: 320,
		bottom: 15,
		left: 90,
		'&::before': {
			bottom: 15,
		},
		'&::after': {
			bottom: 15,
		},
	},
}));

const MessageContent = styled(Box)(({ theme }) => ({
	padding: '24px 20px 20px',
	position: 'relative',
	[theme.breakpoints.down('md')]: {
		padding: '20px 16px 16px',
	},
}));

const MessageText = styled(Typography)(({ theme }) => ({
	margin: 0,
	lineHeight: 1.7,
	color: '#2c3e50',
	fontSize: 15,
	fontWeight: 400,
	textAlign: 'left',
	fontFamily:
		'"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
	letterSpacing: '0.01em',
	[theme.breakpoints.down('md')]: {
		fontSize: 14,
		lineHeight: 1.6,
	},
}));

const TypingCursor = styled('span')(({ theme }) => ({
	opacity: 1,
	animation: 'blink 1s infinite',
	'@keyframes blink': {
		'0%, 50%': { opacity: 1 },
		'51%, 100%': { opacity: 0 },
	},
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
	position: 'absolute',
	top: 8,
	right: 8,
	width: 24,
	height: 24,
	background: 'transparent',
	color: '#9ca3af',
	willChange: 'transform',
	backfaceVisibility: 'hidden',
	'&:hover': {
		background: 'rgba(0, 0, 0, 0.04)',
		color: '#6b7280',
		transform: 'scale3d(1.1, 1.1, 1)',
	},
	transition:
		'transform 0.15s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.15s ease, color 0.15s ease',
}));

const Socrates = ({ currentUser }) => {
	const [isVisible, setIsVisible] = useState(false);
	const [isOfferDialogOpen, setIsOfferDialogOpen] = useState(false);
	const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
	const [hasShownFirstMessage, setHasShownFirstMessage] = useState(false);
	const [displayText, setDisplayText] = useState('');
	const [isTyping, setIsTyping] = useState(false);
	const [isHovering, setIsHovering] = useState(false);

	const socratesUrl = `${process.env.REACT_APP_SOCRATES_URL}/login?token=${
		storageService.load().accessKey
	}`;
	const hasSocratesAddOn = () => {
		if (
			currentUser.addOnList.some(
				(addOn) =>
					addOn.type === AddOnType.SOCRATES &&
					addOn.accountType === AddOnAccountType.LOGIN
			)
		) {
			return true;
		} else {
			return false;
		}
	};

	// Check if user has recently closed messages
	const hasUserClosedMessages = () => {
		const lastClosed = localStorage.getItem('socrates-messages-closed');
		if (!lastClosed) return false;

		const lastClosedTime = parseInt(lastClosed);
		const now = Date.now();
		const hoursSinceClosed = (now - lastClosedTime) / (1000 * 60 * 60);

		// Don't show messages again for 30 days after user closes them
		return hoursSinceClosed < 24 * 30;
	};

	const messages = useMemo(
		() => [
			'Hej, det är jag som är Socrates – din juridiska AI-assistent! Du kanske minns mig från 1989, då jag var frontfigur i den tryckta användarhandboken. Mycket har hänt sedan dess - men nu är jag tillbaka, i digital form och smartare än någonsin.',
			'Jag är din outtröttliga följeslagare, alltid tillgänglig dygnet runt.',
			'Jag sammanställer information snabbt och blir därmed din assistent på vägen mot rättssäkra beslut!',
			'Behöver du hjälp att snabbt hitta rätt bland 35 års samlad juridisk kunskap? Jag söker, sorterar och presenterar svaren – så att du sparar tid och kan fokusera på det viktiga.',
			'Jag levererar källhänvisningar, stöd för beslut och hjälper dig i din research. Kort sagt: jag gör jobbet smidigare, enklare och snabbare.',
			'Snart finns jag i databasen. Följ med på vår resa och boka en demo och upptäck hur jag kan hjälpa just dig.',
		],
		[]
	);

	useEffect(() => {
		// Only show messages if user hasn't recently closed them
		if (hasUserClosedMessages()) {
			return;
		}

		// Show the first message after a delay
		const timer = setTimeout(() => {
			setIsVisible(true);
			setHasShownFirstMessage(true);
		}, 2000);

		return () => clearTimeout(timer);
	}, []);

	// Optimized typing effect using requestAnimationFrame
	const typingTimerRef = useRef(null);
	const startTimeRef = useRef(null);

	useEffect(() => {
		if (!isVisible) return;

		const currentMessage = messages[currentMessageIndex];
		setDisplayText('');
		setIsTyping(true);

		const typingSpeed = 50; // milliseconds per character (reduced for smoother effect)
		const totalDuration = currentMessage.length * typingSpeed;

		const typeMessage = (timestamp) => {
			if (!startTimeRef.current) {
				startTimeRef.current = timestamp;
			}

			const elapsed = timestamp - startTimeRef.current;
			const progress = Math.min(elapsed / totalDuration, 1);
			const charIndex = Math.floor(progress * currentMessage.length);

			setDisplayText(currentMessage.slice(0, charIndex));

			if (progress < 1) {
				typingTimerRef.current = requestAnimationFrame(typeMessage);
			} else {
				setIsTyping(false);
				startTimeRef.current = null;
			}
		};

		typingTimerRef.current = requestAnimationFrame(typeMessage);

		return () => {
			if (typingTimerRef.current) {
				cancelAnimationFrame(typingTimerRef.current);
			}
			startTimeRef.current = null;
		};
	}, [currentMessageIndex, isVisible, messages]);

	useEffect(() => {
		if (!hasShownFirstMessage) return;

		// Calculate total time for typing + pause
		const currentMessage = messages[currentMessageIndex];
		const typingTime = currentMessage.length * 50; // 50ms per character (updated to match new typing speed)
		const pauseTime = 30000; // 30 seconds pause after typing
		const totalTime = typingTime + pauseTime;

		// Rotate messages after typing is complete + pause
		const timeout = setTimeout(() => {
			setCurrentMessageIndex((prevIndex) =>
				prevIndex === messages.length - 1 ? 0 : prevIndex + 1
			);
		}, totalTime);

		return () => clearTimeout(timeout);
	}, [hasShownFirstMessage, currentMessageIndex, messages]);

	const handleButtonClick = useCallback(() => {
		if (hasSocratesAddOn()) {
			window.open(socratesUrl, '_blank');
		} else {
			setIsOfferDialogOpen(true);
		}
	}, []);

	const handleCloseMessage = useCallback(() => {
		setIsVisible(false);
		// Remember when user closed messages
		localStorage.setItem('socrates-messages-closed', Date.now().toString());
	}, []);

	const handleCloseDialog = useCallback(() => {
		setIsOfferDialogOpen(false);
	}, []);

	const handleButtonMouseEnter = useCallback(() => {
		setIsHovering(true);
		setIsVisible(true);
		setHasShownFirstMessage(true);
		localStorage.removeItem('socrates-messages-closed');
	}, []);

	const handleButtonMouseLeave = useCallback(() => {
		setIsHovering(false);
		// Don't hide messages immediately on mouse leave, let user close them manually
	}, []);

	return (
		<Box>
			{!hasSocratesAddOn() && (
				<SocratesOfferDialog
					open={isOfferDialogOpen}
					onClose={handleCloseDialog}
				/>
			)}
			<StyledFab
				onClick={handleButtonClick}
				onMouseEnter={handleButtonMouseEnter}
				onMouseLeave={handleButtonMouseLeave}
				aria-label="Socrates AI Assistant"
			>
				<StyledIcon src="/Socrates_ikon.png" alt="Socrates" />
			</StyledFab>

			{!hasSocratesAddOn() && (
				<Fade
					in={isVisible}
					timeout={400}
					sx={{
						position: 'fixed',
						zIndex: 1200,
						bottom: 110,
						left: 130,
					}}
				>
					<Box>
						<MessagePaper>
							<MessageContent>
								<MessageText variant="body2">
									{displayText}
									{isTyping && <TypingCursor>|</TypingCursor>}
								</MessageText>
								<CloseButton
									onClick={handleCloseMessage}
									aria-label="Stäng meddelande"
									size="small"
								>
									<CloseIcon fontSize="small" />
								</CloseButton>
							</MessageContent>
						</MessagePaper>
					</Box>
				</Fade>
			)}
		</Box>
	);
};

export default Socrates;
